
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Gift, Plus, Edit, Save } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import TremendousIntegrationSettings from './TremendousIntegrationSettings';

const RewardIntegrationsTab: React.FC = () => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [integrationName, setIntegrationName] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiEndpoint, setApiEndpoint] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [storeKey, setStoreKey] = useState('');

  const handleAddIntegration = () => {
    if (!integrationName.trim()) {
      toast({
        title: "Error",
        description: "Please enter an integration name",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    
    // Simulate API call for adding a new integration
    setTimeout(() => {
      toast({
        title: "Success",
        description: `${integrationName} integration has been added.`,
      });
      setIsSubmitting(false);
      setOpen(false);
      
      // Reset form
      setIntegrationName('');
      setApiKey('');
      setApiEndpoint('');
    }, 1500);
  };

  const handleEditSettings = () => {
    setIsEditing(true);
  };

  const handleSaveSettings = () => {
    setIsEditing(false);
    toast({
      title: "Success",
      description: "Reward integration settings have been saved.",
    });
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  return (
    <div className="space-y-6">
      {/* Tremendous Integration */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="p-2 rounded-full bg-purple-100">
                <Gift className="h-5 w-5 text-purple-600" />
              </div>
              <CardTitle>Tremendous Integration</CardTitle>
            </div>
            {isEditing ? (
              <div className="flex space-x-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </Button>
                <Button 
                  size="sm"
                  onClick={handleSaveSettings}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </Button>
              </div>
            ) : (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleEditSettings}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            )}
          </div>
          <CardDescription>
            Configure Tremendous for sending gift cards and rewards
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="store-key">Store Key (Optional)</Label>
            <Input
              id="store-key"
              value={storeKey}
              onChange={(e) => setStoreKey(e.target.value)}
              placeholder="Enter store key"
              disabled={!isEditing}
            />
            <p className="text-xs text-muted-foreground">
              Optional key for store-specific reward configurations
            </p>
          </div>
          <TremendousIntegrationSettings isEditing={isEditing} />
        </CardContent>
      </Card>
      
      {/* Add New Integration Button */}
      <Card>
        <CardHeader>
          <CardTitle>Add New Integration</CardTitle>
          <CardDescription>
            Connect additional reward providers to your platform
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="w-full sm:w-auto flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Add New Reward Integration
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Add Reward Provider</DialogTitle>
                <DialogDescription>
                  Enter the details for the new reward provider integration.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="name" className="text-right">
                    Name
                  </Label>
                  <Input
                    id="name"
                    value={integrationName}
                    onChange={(e) => setIntegrationName(e.target.value)}
                    className="col-span-3"
                    placeholder="Integration name"
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="store-key-new" className="text-right">
                    Store Key
                  </Label>
                  <Input
                    id="store-key-new"
                    value={storeKey}
                    onChange={(e) => setStoreKey(e.target.value)}
                    className="col-span-3"
                    placeholder="Optional store key"
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="apiKey" className="text-right">
                    API Key
                  </Label>
                  <Input
                    id="apiKey"
                    type="password"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    className="col-span-3"
                    placeholder="Your API key"
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="endpoint" className="text-right">
                    API Endpoint
                  </Label>
                  <Input
                    id="endpoint"
                    value={apiEndpoint}
                    onChange={(e) => setApiEndpoint(e.target.value)}
                    className="col-span-3"
                    placeholder="https://api.example.com/v1"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={handleAddIntegration} disabled={isSubmitting}>
                  {isSubmitting ? "Adding..." : "Add Integration"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    </div>
  );
};

export default RewardIntegrationsTab;
